import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { money, shortDate } from "@/lib/invoice";

const AGING = [["current", "Current"], ["31_60", "31–60 days"], ["61_90", "61–90 days"], ["over_90", "Over 90 days"]];

export default function StatementPrint() {
  const [params] = useSearchParams();
  const [list, setList] = useState(null);
  const all = params.get("all") === "1";

  useEffect(() => {
    const req = all ? api.get("/statements/all").then((r) => r.data) : api.get("/statement", { params: { customer: params.get("customer") } }).then((r) => [r.data]);
    req.then(setList).catch((e) => toast.error(formatError(e)));
  }, [params, all]);
  useEffect(() => {
    if (!list?.length) return undefined;
    const t = setTimeout(() => window.print(), 700);
    return () => clearTimeout(t);
  }, [list]);

  if (!list) return <div className="min-h-screen bg-white text-black p-10" data-testid="statement-loading">Loading statement…</div>;
  return (
    <div className="min-h-screen bg-white text-black" data-testid="statement-page">
      {all && <p className="no-print p-4 text-sm bg-slate-100" data-testid="statement-count">{list.length} statement{list.length === 1 ? "" : "s"} · one per page</p>}
      {list.map((d, i) => <Statement key={i} data={d} />)}
      {!list.length && <p className="p-10" data-testid="statement-none">No customers have open invoices.</p>}
    </div>
  );
}

function Statement({ data }) {
  const { customer: c, settings: s } = data;
  return (
    <div className="print-page">
      <div className="mx-auto p-10" style={{ maxWidth: 816, fontFamily: "'Times New Roman', Tinos, Times, serif" }} data-testid="statement-sheet">
        <div className="flex justify-between items-start border-b-2 border-black pb-3">
          <div>
            <p className="italic font-bold text-[26px]">{s.company_name}</p>
            <p className="text-[14px]">{s.address_line1}</p>
            <p className="text-[14px]">{s.address_line2}</p>
            <p className="text-[14px]">{[s.phone, s.toll_free, s.email].filter(Boolean).join(" · ")}</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-[28px] tracking-wide">STATEMENT</p>
            <p className="text-[14px]">Date: {shortDate(data.date)}</p>
          </div>
        </div>
        <div className="mt-5 text-[15px]" data-testid="statement-customer">
          <p className="font-bold">{c.name}</p>
          {c.address && <p>{c.address}</p>}
          {c.city_state_zip && <p>{c.city_state_zip}</p>}
          {c.phone && <p>{c.phone}</p>}
          {c.administrator && <p>Attn: {c.administrator}</p>}
        </div>
        <table className="w-full mt-6 text-[15px] border-collapse" data-testid="statement-invoices">
          <thead>
            <tr className="border-y border-black text-left">
              <th className="py-1.5">Invoice #</th><th className="py-1.5">Service date</th><th className="py-1.5">Days</th><th className="py-1.5 text-right">Amount due</th>
            </tr>
          </thead>
          <tbody>
            {data.invoices.map((i) => (
              <tr key={i.id} className="border-b border-slate-300" data-testid={`statement-row-${i.number}`}>
                <td className="py-1.5 font-mono">{i.number}</td><td className="py-1.5">{shortDate(i.service_date)}</td>
                <td className="py-1.5">{i.days ?? "—"}</td><td className="py-1.5 text-right">{money(i.amount)}</td>
              </tr>
            ))}
            {!data.invoices.length && <tr><td colSpan={4} className="py-4 text-center" data-testid="statement-empty">No open invoices — thank you!</td></tr>}
          </tbody>
        </table>
        <div className="mt-6 grid grid-cols-5 border border-black text-center text-[14px]" data-testid="statement-aging">
          {AGING.map(([k, label]) => (
            <div key={k} className="border-r border-black p-2"><p className="font-bold">{label}</p><p>{money(data.aging[k])}</p></div>
          ))}
          <div className="p-2 bg-slate-100"><p className="font-bold">Total due</p><p className="font-bold" data-testid="statement-total">{money(data.total)}</p></div>
        </div>
        {s.terms && <p className="mt-6 text-[13px] whitespace-pre-line">{s.terms}</p>}
        <p className="mt-4 text-[13px]">Please make checks payable to {s.company_name}. Thank you for your business.</p>
      </div>
    </div>
  );
}
